import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { startOfDay, startOfMonth, endOfDay, subDays, format, subMonths } from "date-fns";
import { getPeopleCount } from "@/lib/ticket-utils";

interface HourlySales {
  hour: string;
  total: number;
  count: number;
}

interface DailyTrend {
  date: string;
  total: number;
  count: number;
}

interface DashboardAnalytics {
  // Today metrics
  ordersToday: number;
  salesToday: number;
  averageTicketToday: number;
  
  // Month metrics
  ordersThisMonth: number;
  salesThisMonth: number;
  averageTicketMonth: number;
  
  // Comparisons
  salesGrowthPercent: number; // vs last month
  ordersGrowthPercent: number;
  
  // Totals
  activeProducts: number;
  activeCategories: number;
  lowStockItems: number;
  
  // Charts data
  hourlySales: HourlySales[];
  last7DaysTrend: DailyTrend[];
  
  // Goal
  monthlyGoal: number;
  goalProgress: number;
  
  isLoading: boolean;
}

export function useDashboardAnalytics(): DashboardAnalytics {
  const { activeRestaurant } = useRestaurantContext();
  const [analytics, setAnalytics] = useState<DashboardAnalytics>({
    ordersToday: 0,
    salesToday: 0,
    averageTicketToday: 0,
    ordersThisMonth: 0,
    salesThisMonth: 0,
    averageTicketMonth: 0,
    salesGrowthPercent: 0,
    ordersGrowthPercent: 0,
    activeProducts: 0,
    activeCategories: 0,
    lowStockItems: 0,
    hourlySales: [],
    last7DaysTrend: [],
    monthlyGoal: 0,
    goalProgress: 0,
    isLoading: true,
  });

  useEffect(() => {
    if (!activeRestaurant) {
      setAnalytics((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const fetchAnalytics = async () => {
      setAnalytics((prev) => ({ ...prev, isLoading: true }));

      try {
        const now = new Date();
        const todayStart = startOfDay(now).toISOString();
        const todayEnd = endOfDay(now).toISOString();
        const monthStart = startOfMonth(now).toISOString();
        const lastMonthStart = startOfMonth(subMonths(now, 1)).toISOString();
        const lastMonthEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)).toISOString();
        const last7DaysStart = startOfDay(subDays(now, 6)).toISOString();

        // Fetch all data in parallel
        const [
          productsResult,
          categoriesResult,
          lowStockIngredientsResult,
          lowStockProductsResult,
          ordersTodayResult,
          ordersMonthResult,
          ordersLastMonthResult,
          last7DaysResult,
          goalResult,
        ] = await Promise.all([
          // Active products count
          supabase
            .from("products")
            .select("*", { count: "exact", head: true })
            .eq("restaurant_id", activeRestaurant.id)
            .eq("is_active", true),

          // Active categories count
          supabase
            .from("categories")
            .select("*", { count: "exact", head: true })
            .eq("restaurant_id", activeRestaurant.id)
            .eq("is_active", true),

          // Low stock ingredients (fetch all with minimum_stock > 0)
          supabase
            .from("ingredients")
            .select("current_stock, minimum_stock")
            .eq("restaurant_id", activeRestaurant.id)
            .eq("is_active", true)
            .gt("minimum_stock", 0),

          // Low stock products (tracks_stock = true)
          supabase
            .from("products")
            .select("current_stock, minimum_stock")
            .eq("restaurant_id", activeRestaurant.id)
            .eq("is_active", true)
            .eq("tracks_stock", true)
            .gt("minimum_stock", 0),

          // Orders today with time for hourly breakdown
          supabase
            .from("orders")
            .select("total, created_at, people_count, order_type")
            .eq("restaurant_id", activeRestaurant.id)
            .gte("created_at", todayStart)
            .lte("created_at", todayEnd)
            .neq("status", "cancelled"),

          // Orders this month
          supabase
            .from("orders")
            .select("total, people_count, order_type")
            .eq("restaurant_id", activeRestaurant.id)
            .gte("created_at", monthStart)
            .neq("status", "cancelled"),

          // Orders last month (for comparison)
          supabase
            .from("orders")
            .select("total, people_count, order_type")
            .eq("restaurant_id", activeRestaurant.id)
            .gte("created_at", lastMonthStart)
            .lte("created_at", lastMonthEnd)
            .neq("status", "cancelled"),

          // Last 7 days orders
          supabase
            .from("orders")
            .select("total, created_at")
            .eq("restaurant_id", activeRestaurant.id)
            .gte("created_at", last7DaysStart)
            .neq("status", "cancelled"),

          // Monthly goal
          supabase
            .from("sales_goals" as any)
            .select("goal_amount")
            .eq("restaurant_id", activeRestaurant.id)
            .eq("month", now.getMonth() + 1)
            .eq("year", now.getFullYear())
            .maybeSingle(),
        ]);

        // Calculate low stock count client-side (comparing current_stock < minimum_stock)
        const lowIngredients = (lowStockIngredientsResult.data || [])
          .filter(i => Number(i.current_stock) < Number(i.minimum_stock)).length;
        const lowProducts = (lowStockProductsResult.data || [])
          .filter(p => Number(p.current_stock) < Number(p.minimum_stock)).length;
        const lowStockItems = lowIngredients + lowProducts;

        // Calculate today metrics
        const salesToday = ordersTodayResult.data?.reduce(
          (sum, order) => sum + Number(order.total), 0
        ) || 0;
        const ordersToday = ordersTodayResult.data?.length || 0;
        const peopleTodayTotal = ordersTodayResult.data?.reduce(
          (sum, order) => sum + getPeopleCount(order), 0
        ) || 0;
        const averageTicketToday = peopleTodayTotal > 0 ? salesToday / peopleTodayTotal : 0;

        // Calculate hourly sales
        const hourlyMap = new Map<number, { total: number; count: number }>();
        ordersTodayResult.data?.forEach((order) => {
          const hour = new Date(order.created_at).getHours();
          const existing = hourlyMap.get(hour) || { total: 0, count: 0 };
          hourlyMap.set(hour, {
            total: existing.total + Number(order.total),
            count: existing.count + 1,
          });
        });

        const hourlySales: HourlySales[] = [];
        for (let h = 6; h <= 23; h++) {
          const data = hourlyMap.get(h) || { total: 0, count: 0 };
          hourlySales.push({
            hour: `${h}h`,
            total: data.total,
            count: data.count,
          });
        }

        // Calculate month metrics
        const salesThisMonth = ordersMonthResult.data?.reduce(
          (sum, order) => sum + Number(order.total), 0
        ) || 0;
        const ordersThisMonth = ordersMonthResult.data?.length || 0;
        const peopleMonthTotal = ordersMonthResult.data?.reduce(
          (sum, order) => sum + getPeopleCount(order), 0
        ) || 0;
        const averageTicketMonth = peopleMonthTotal > 0 ? salesThisMonth / peopleMonthTotal : 0;

        // Calculate last month metrics
        const salesLastMonth = ordersLastMonthResult.data?.reduce(
          (sum, order) => sum + Number(order.total), 0
        ) || 0;
        const ordersLastMonth = ordersLastMonthResult.data?.length || 0;

        // Calculate growth percentages
        const salesGrowthPercent = salesLastMonth > 0
          ? ((salesThisMonth - salesLastMonth) / salesLastMonth) * 100
          : 0;
        const ordersGrowthPercent = ordersLastMonth > 0
          ? ((ordersThisMonth - ordersLastMonth) / ordersLastMonth) * 100
          : 0;

        // Calculate last 7 days trend
        const dailyMap = new Map<string, { total: number; count: number }>();
        last7DaysResult.data?.forEach((order) => {
          const date = format(new Date(order.created_at), "yyyy-MM-dd");
          const existing = dailyMap.get(date) || { total: 0, count: 0 };
          dailyMap.set(date, {
            total: existing.total + Number(order.total),
            count: existing.count + 1,
          });
        });

        const last7DaysTrend: DailyTrend[] = [];
        for (let i = 6; i >= 0; i--) {
          const date = subDays(now, i);
          const dateStr = format(date, "yyyy-MM-dd");
          const data = dailyMap.get(dateStr) || { total: 0, count: 0 };
          last7DaysTrend.push({
            date: format(date, "dd/MM"),
            total: data.total,
            count: data.count,
          });
        }

        // Goal progress
        const monthlyGoal = (goalResult.data as any)?.goal_amount || 0;
        const goalProgress = monthlyGoal > 0 ? (salesThisMonth / monthlyGoal) * 100 : 0;

        setAnalytics({
          ordersToday,
          salesToday,
          averageTicketToday,
          ordersThisMonth,
          salesThisMonth,
          averageTicketMonth,
          salesGrowthPercent,
          ordersGrowthPercent,
          activeProducts: productsResult.count || 0,
          activeCategories: categoriesResult.count || 0,
          lowStockItems,
          hourlySales,
          last7DaysTrend,
          monthlyGoal,
          goalProgress,
          isLoading: false,
        });
      } catch (error) {
        console.error("Erro ao buscar analytics:", error);
        setAnalytics((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchAnalytics();
  }, [activeRestaurant]);

  return analytics;
}
